"use client";

/**
 * WebAuthn prompt component — handles browser biometric UI interactions.
 *
 * Used in two contexts:
 * 1. Registration (navigator.credentials.create) — during MFA setup
 * 2. Authentication (navigator.credentials.get) — during login MFA step
 *
 * On browser cancellation or hardware failure, calls onFallback() so the
 * caller can show the TOTP form instead. Never forces the user to retry.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { beginWebAuthnAuthentication, beginWebAuthnRegistration } from "@/lib/api/auth";
import { APIClientError } from "@/lib/api/client";
import { Spinner } from "@/components/ui/spinner";

// ── Types ─────────────────────────────────────────────────────────────────────

interface WebAuthnRegisterProps {
  mode: "register";
  /** Called with the raw PublicKeyCredential JSON on success. */
  onSuccess: (credential: Record<string, unknown>) => void;
  /** Called when the user cancels or hardware is unavailable. */
  onFallback?: () => void;
  onError?: (message: string) => void;
}

interface WebAuthnAuthenticateProps {
  mode: "authenticate";
  userId: string;
  onSuccess: (credential: Record<string, unknown>) => void;
  onFallback?: () => void;
  onError?: (message: string) => void;
}

type WebAuthnPromptProps = WebAuthnRegisterProps | WebAuthnAuthenticateProps;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Convert an ArrayBuffer to a base64url string. */
function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Serialize a PublicKeyCredential into a plain JSON object.
 *
 * PublicKeyCredential properties (id, rawId, type, response) are prototype
 * getters that don't appear in Object.entries(). We must extract them explicitly.
 */
function serializeCredential(credential: Credential): Record<string, unknown> {
  const pkc = credential as PublicKeyCredential;
  const response = pkc.response;

  const result: Record<string, unknown> = {
    id: pkc.id,
    rawId: bufferToBase64url(pkc.rawId),
    type: pkc.type,
    authenticatorAttachment: pkc.authenticatorAttachment ?? undefined,
  };

  if (response instanceof AuthenticatorAttestationResponse) {
    // Registration response
    result.response = {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      // getTransports() is available on attestation responses
      transports: typeof response.getTransports === "function"
        ? response.getTransports()
        : [],
    };
  } else if (response instanceof AuthenticatorAssertionResponse) {
    // Authentication response
    result.response = {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    };
  }

  return result;
}

/**
 * Convert the backend's base64url-encoded challenge/ids back to ArrayBuffer
 * for use with navigator.credentials.
 */
function base64urlToBuffer(base64url: string): ArrayBuffer {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padLength = (4 - (padded.length % 4)) % 4;
  const base64 = padded + "=".repeat(padLength);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Recursively convert base64url strings in the options object to ArrayBuffers
 * for the fields WebAuthn expects as binary (challenge, id, allowCredentials[].id, etc.)
 */
function prepareOptions(
  options: Record<string, unknown>,
  parentKey?: string,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(options)) {
    if (key === "challenge" && typeof value === "string") {
      result[key] = base64urlToBuffer(value);
    } else if (key === "id" && typeof value === "string" && parentKey === "user") {
      // Only decode user.id (base64url UUID), not rp.id (domain string)
      result[key] = base64urlToBuffer(value);
    } else if (key === "allowCredentials" && Array.isArray(value)) {
      result[key] = value.map((cred: Record<string, unknown>) => ({
        ...cred,
        id: typeof cred.id === "string" ? base64urlToBuffer(cred.id) : cred.id,
      }));
    } else if (key === "excludeCredentials" && Array.isArray(value)) {
      result[key] = value.map((cred: Record<string, unknown>) => ({
        ...cred,
        id: typeof cred.id === "string" ? base64urlToBuffer(cred.id) : cred.id,
      }));
    } else if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      result[key] = prepareOptions(value as Record<string, unknown>, key);
    } else {
      result[key] = value;
    }
  }
  return result;
}

/** Returns true if the error is a user cancellation (NotAllowedError). */
function isUserCancellation(err: unknown): boolean {
  return err instanceof DOMException && err.name === "NotAllowedError";
}

// ── Component ─────────────────────────────────────────────────────────────────

export function WebAuthnPrompt(props: WebAuthnPromptProps) {
  const [status, setStatus] = useState<"idle" | "waiting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const triggeredRef = useRef(false);

  const trigger = useCallback(async () => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    setStatus("waiting");
    setErrorMsg(null);

    try {
      if (props.mode === "register") {
        // ── Registration ──────────────────────────────────────────────────
        const rawOptions = await beginWebAuthnRegistration();
        const prepared = prepareOptions(rawOptions as Record<string, unknown>);

        const credential = await navigator.credentials.create({
          publicKey: prepared as unknown as PublicKeyCredentialCreationOptions,
        });

        if (!credential) {
          throw new DOMException("No credential returned", "NotAllowedError");
        }

        setStatus("success");
        props.onSuccess(serializeCredential(credential) as Record<string, unknown>);
      } else {
        // ── Authentication ────────────────────────────────────────────────
        const rawOptions = await beginWebAuthnAuthentication(props.userId);
        const prepared = prepareOptions(rawOptions as Record<string, unknown>);

        const credential = await navigator.credentials.get({
          publicKey: prepared as unknown as PublicKeyCredentialRequestOptions,
        });

        if (!credential) {
          throw new DOMException("No credential returned", "NotAllowedError");
        }

        setStatus("success");
        props.onSuccess(serializeCredential(credential) as Record<string, unknown>);
      }
    } catch (err) {
      triggeredRef.current = false; // Allow retry
      if (isUserCancellation(err)) {
        if (props.onFallback) {
          // User cancelled — show fallback (e.g. TOTP form)
          props.onFallback();
        } else {
          // No fallback available — reset to idle so user can retry or navigate away
          setStatus("idle");
        }
        return;
      }
      // Hardware/browser/network error — show message
      const msg =
        err instanceof APIClientError
          ? err.message
          : err instanceof DOMException
            ? `Biometric authentication unavailable: ${err.message}`
            : "Something went wrong. Please try again.";
      setErrorMsg(msg);
      setStatus("error");
      props.onError?.(msg);
    }
  }, [props]);

  // Auto-trigger when in authenticate mode (login MFA step)
  useEffect(() => {
    if (props.mode === "authenticate") {
      void trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (props.mode === "register") {
    // In register mode, the parent controls when to call trigger().
    // We expose the button UI.
    return (
      <div className="space-y-3">
        {status === "waiting" && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 text-sm text-text-secondary">
            <Spinner size="sm" />
            <span>Waiting for biometric confirmation…</span>
          </div>
        )}
        {status === "success" && (
          <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-sm text-success">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4 shrink-0">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
            </svg>
            Fingerprint registered
          </div>
        )}
        {status === "error" && errorMsg && (
          <div className="rounded-lg bg-error-light px-3 py-2.5 text-sm text-error">
            {errorMsg}
          </div>
        )}
        {status !== "waiting" && status !== "success" && (
          <button
            type="button"
            onClick={() => void trigger()}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-text shadow-sm hover:bg-accent-hover focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface focus:outline-none"
          >
            <FingerprintIcon className="size-4" />
            {status === "error" ? "Try again" : "Use Touch ID / Windows Hello"}
          </button>
        )}
      </div>
    );
  }

  // Authenticate mode — shows inline state while browser dialog is open
  if (status === "waiting") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 text-sm text-text-secondary">
        <Spinner size="sm" />
        <span>Waiting for fingerprint…</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="space-y-3">
        {errorMsg && (
          <div className="rounded-lg bg-error-light px-3 py-2.5 text-sm text-error">
            {errorMsg}
          </div>
        )}
        <button
          type="button"
          onClick={() => void trigger()}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary focus:outline-none"
        >
          <FingerprintIcon className="size-4" />
          Retry fingerprint
        </button>
      </div>
    );
  }

  return null; // success or idle — parent handles redirect
}

function FingerprintIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
      <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
      <path d="M2 12a10 10 0 0 1 18-6" />
      <path d="M2 16h.01" />
      <path d="M21.8 16c.2-2 .131-5.354 0-6" />
      <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
      <path d="M8.65 22c.21-.66.45-1.32.57-2" />
      <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
    </svg>
  );
}
